import React, { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import client from "../../shared/api/client";
import { toastError } from "../../shared/toastHelper";
import { StaffingDepartmentsContext } from "./components/StaffingDepartmentsContext";
import DepartmentsBoard from "./components/DepartmentsBoard";
import { errorMessages } from "./components/staffingFormatters";
import { departmentShape } from "./components/staffingShapes";

const StaffingDepartmentsApp = ({ departments: initialDepartments, tabPaths, permissions, permissionScopes, paths }) => {
  const [departments, setDepartments] = useState(initialDepartments);

  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);

  const mutate = useCallback(async (method, url, body, failureMessage) => {
    if (inFlight.current) return { ok: false, errors: [] };
    inFlight.current = true;
    setPending(true);
    try {
      const { data } = await client[method](url, body);
      setDepartments(data.departments);
      return { ok: true, errors: [] };
    } catch (error) {
      const errors = errorMessages(error);
      toastError(errors.length ? errors.join(" ") : failureMessage);
      return { ok: false, errors };
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }, []);

  const value = useMemo(
    () => ({ departments, tabPaths, permissions, permissionScopes, paths, mutate, pending }),
    [departments, tabPaths, permissions, permissionScopes, paths, mutate, pending]
  );

  return (
    <StaffingDepartmentsContext.Provider value={value}>
      <DepartmentsBoard />
      <ToastContainer icon={false} />
    </StaffingDepartmentsContext.Provider>
  );
};

StaffingDepartmentsApp.propTypes = {
  departments: PropTypes.arrayOf(departmentShape).isRequired,
  tabPaths: PropTypes.object.isRequired,
  permissions: PropTypes.shape({
    manageRoster: PropTypes.bool.isRequired,
    manageRates: PropTypes.bool.isRequired,
  }).isRequired,
  permissionScopes: PropTypes.shape({
    managePositionIds: PropTypes.arrayOf(PropTypes.number),
    manageDepartmentIds: PropTypes.arrayOf(PropTypes.number),
    ratePositionIds: PropTypes.arrayOf(PropTypes.number),
  }),
  paths: PropTypes.shape({
    index: PropTypes.string.isRequired,
    departments: PropTypes.string.isRequired,
    positions: PropTypes.string.isRequired,
  }).isRequired,
};

export default StaffingDepartmentsApp;
